<template>
  <div class="identity">
    <span class="avatar lg" :style="{ background: avatarBg }">{{ initials }}</span>
    <div class="idText">
      <div class="idName">{{ entry?.contact_name || 'Unknown contact' }}</div>
      <div class="idMeta">{{ meta }}</div>
      <CrmLinkChips :crm-ids="entry?.crm_ids ?? null" />
      <div v-if="signalChips.length" class="signals">
        <span
          v-for="(ch, i) in signalChips"
          :key="i"
          class="signalChip"
          :class="ch.polarity"
          :title="ch.summary || undefined"
        >
          <AppIcon name="signal" :size="13" />{{ ch.text }}
        </span>
      </div>
    </div>
    <div v-if="$slots.actions" class="idActions"><slot name="actions" /></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import CrmLinkChips from './CrmLinkChips.vue'
import { formatLocalTime, relativeAgo } from '../../utils/localTime'
import type { CallerQueueEntry } from '../../../services/dialerApi'

// The PERSON region — avatar, name, title/company/local-time meta, CRM links,
// and the recent engagement chips. Everything about the human, in one place;
// the numbers live in PhoneLines and the company/play background in AboutBriefs.
const props = defineProps<{
  entry: CallerQueueEntry | null
}>()

const avatarBg = computed(() =>
  props.entry?.label === 'cold' ? 'var(--hue-gray-soft)' : 'var(--hue-amber-soft)',
)

const initials = computed(() => {
  const n = String(props.entry?.contact_name || '').trim()
  if (!n) return '?'
  const p = n.split(/\s+/).filter(Boolean)
  return ((p[0]?.[0] || '') + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase() || '?'
})

const meta = computed(() => {
  if (!props.entry) return ''
  return [props.entry.contact_title, props.entry.account_name, formatLocalTime(props.entry.timezone)]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' · ')
})

// All the signals the server curated (it already caps and ranks them — no
// second cap here). polarity tints the chip; summary rides as the tooltip.
const signalChips = computed(() => {
  const sigs = props.entry?.top_signals || []
  return sigs.map((s) => {
    const label =
      (s.label && String(s.label).trim()) || String(s.kind || 'signal').replace(/_/g, ' ').trim() || 'signal'
    const age = relativeAgo(s.occurred_at)
    return {
      text: age ? `${label} · ${age}` : label,
      polarity: s.polarity === 'positive' || s.polarity === 'negative' ? s.polarity : '',
      summary: s.summary,
    }
  })
})
</script>

<style scoped>
/* row-gap is the wrapped-state clearance between a trailing action and the CRM /
   signal chips above it — both are click targets, and the action dials. */
.identity { display: flex; align-items: flex-start; column-gap: var(--space-3); row-gap: var(--space-5); flex-wrap: wrap; }
.avatar { display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; flex-shrink: 0; font-weight: var(--fw-semibold); color: var(--color-text-primary); }
.avatar.lg { width: 44px; height: 44px; font-size: var(--fs-base); }
.idText { flex: 1 1 160px; min-width: 0; }
.idActions { flex-shrink: 0; margin-left: auto; align-self: flex-start; }
.idName { font-size: var(--fs-md); font-weight: var(--fw-semibold); }
.idMeta { margin-top: 2px; font-size: var(--fs-sm); color: var(--color-text-secondary); }

.signals { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3); }
.signalChip { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: var(--radius-full); background: var(--color-bg-subtle); font-size: var(--fs-xs); color: var(--color-text-secondary); }
.signalChip.negative { background: var(--color-danger-soft); color: var(--color-danger); }
</style>
